import React, { ComponentProps, useMemo } from "react";
import { cn } from "@/utils/cn";

type Option = {
  label: string;
  value: string;
  description?: string;
  disabled?: boolean;
  className?: string;
  inputClassName?: string;
};

type Props<T extends Option> = {
  options: T[];
  selectedValue?: string;
  onChange?: (value: string) => void;
  onSelect?: (value: string) => void;
  label?: string;
  error?:
    | string[]
    | string
    | {
        message: string;
      };
  rootClassName?: string;
  className?: string;
} & Omit<ComponentProps<"input">, "onChange" | "onSelect">;

const Radio = <T extends Option>({
  options,
  selectedValue,
  onChange,
  onSelect,
  error,
  label,
  rootClassName,
  className,
  ...rest
}: Props<T>) => {
  const errorValue = useMemo(() => {
    if (!error) return null;
    if (typeof error === "string") return error;
    if (Array.isArray(error)) return error[0];
    return error.message;
  }, [error]);

  const handleChange = (value: string, isDisabled?: boolean) => {
    if (isDisabled) return;
    onChange?.(value);
    onSelect?.(value);
  };

  return (
    <div className={rootClassName}>
      {label && (
        <p className="mb-2 text-[0.75rem] text-xs font-medium leading-[0.875rem] text-[#6D7D96]">
          {label}
        </p>
      )}
      <div className={cn("flex flex-col gap-4 md:flex-row md:gap-6", className)}>
        {options.map((option) => (
          <label
            key={option.value}
            htmlFor={option.label}
            className={cn(
              "flex gap-2 text-sm text-dark-4 focus:outline-none",
              option.disabled && "bg-slate-100 text-slate-500 ",
              option.className
            )}
            onClick={() => handleChange(option.value, option.disabled)}
          >
            <input
              {...option}
              type="radio"
              name={option.label}
              value={option.value}
              checked={selectedValue === option.value}
              onChange={(e) => handleChange(e.target.value, option.disabled)}
              className={cn(
                "mt-[1px] cursor-pointer border border-border-subtle focus:outline-none focus:ring-0",
                option.disabled && "bg-border-subtle text-placeholder",
                option.inputClassName
              )}
              {...rest}
            />
            <span className="content-start">
              <p className={cn("text-sm text-dark-4", option.disabled && "text-slate-500")}>
                {option.label}
              </p>
              {option.description && (
                <p className={cn("text-xs text-access-1", option.disabled && "text-slate-400")}>
                  {option.description}
                </p>
              )}
            </span>
          </label>
        ))}
      </div>

      {errorValue && <div className="mt-2 text-sm text-danger-base">{errorValue}</div>}
    </div>
  );
};

export default Radio;
